import {
  createEditorPlatformEditActionsError,
  EditorPlatformEditActionsErrorCode,
} from '../errors';
import { PlatformContext } from '../types';

/*
 * This class is responsible for building the inner elements path array based on the element path.
 *
 * Usage: Forward action in Edit Actions.
 *
 * Example of element path by strategies:
 * 1. root: '.root.navigation.PrevIcon'
 * 2. self: '.self.quantityIncrease'
 *
 */

const INNER_ELEMENTS_PATH_SEPARATOR = '.';

interface PathStrategy {
  build(): string[] | undefined;
}

class RootPathStrategy implements PathStrategy {
  public static HEAD_PATH_ELEMENT = `.root`;

  constructor(
    private readonly elementPath: string,
    private readonly context: PlatformContext,
  ) {}

  build() {
    const [_, elementsPath] = this.elementPath.split(
      RootPathStrategy.HEAD_PATH_ELEMENT,
    );

    const splittedElementPaths = elementsPath.split(
      INNER_ELEMENTS_PATH_SEPARATOR,
    );

    const [rootElementFromPointer] =
      this.context.editorPointerAPI.getInnerElementPath(
        this.context.editorPointer,
      ) || [];

    if (!rootElementFromPointer) {
      return undefined;
    }

    return [rootElementFromPointer, ...splittedElementPaths].filter(Boolean);
  }
}

class SelfPathStrategy implements PathStrategy {
  public static HEAD_PATH_ELEMENT = `.self`;

  constructor(
    private readonly elementPath: string,
    private readonly context: PlatformContext,
  ) {}

  build() {
    const [_, elementsPath] = this.elementPath.split(
      SelfPathStrategy.HEAD_PATH_ELEMENT,
    );

    const splittedElementPaths = elementsPath.split(
      INNER_ELEMENTS_PATH_SEPARATOR,
    );

    const [rootElementFromPointer, ...innerElementsBeforeSelf] =
      this.context.editorPointerAPI.getInnerElementPath(
        this.context.editorPointer,
      ) || [];

    if (!rootElementFromPointer) {
      return undefined;
    }

    return [
      rootElementFromPointer,
      ...innerElementsBeforeSelf,
      ...splittedElementPaths,
    ].filter(Boolean);
  }
}

class InnerElementsPathService {
  private strategy;

  constructor(
    private readonly elementPath: string,
    private readonly context: PlatformContext,
  ) {
    this.strategy = this.getStrategy();
  }

  private getStrategy() {
    if (this.elementPath.startsWith(RootPathStrategy.HEAD_PATH_ELEMENT)) {
      return new RootPathStrategy(this.elementPath, this.context);
    }

    if (this.elementPath.startsWith(SelfPathStrategy.HEAD_PATH_ELEMENT)) {
      return new SelfPathStrategy(this.elementPath, this.context);
    }

    throw createEditorPlatformEditActionsError(
      EditorPlatformEditActionsErrorCode.EditActionForwardElementPathStrategyNotFound,
      `Forward path strategy not found for element path: ${this.elementPath}`,
    );
  }

  build() {
    return this.strategy.build();
  }
}

export { InnerElementsPathService };
